import React from "react";
import { TEXT_LEVELS, Text, plainText, safeElement, type TextLevel } from "../../atoms/text/text";
import { Disclosure } from "./disclosure";
import { GRID_SECTION_OFFSET_PROPERTY } from "./grid-section.shared";
import { isRenderable } from "./is-renderable.shared";
import { LazySectionBody } from "./lazy-section-body.client";
import { SectionScrollKeeper } from "./section-scroll-keeper.client";
import styles from "./grid-section.module.css";
import { withSafeRender } from "../../atoms/safe-render";

/**
 * One collapsible section of the Signature packages grid — GS6-GS9, GS11. The header bar with the
 * section name and its chevron, optionally the ✓/$/★ key under the name (GS9) and an intro above
 * the rows (GS11), then whatever rows the caller hands it.
 */

export interface GridSectionProps {
  /** GS1/GS3 — the section name, drawn in the header bar. Markup in an
   *  authored label renders as plain text (GR9). */
  heading: string;
  /** AX5 — a section name is a real heading. Defaults to `h3`, which sits
   *  under the page's `h1` and the grid's own heading. */
  headingLevel?: TextLevel;
  /** GS7 — the `open` attribute the server emits. See the remount note. */
  defaultOpen?: boolean;
  /**
   * GS9 — the ✓/$/★ key, drawn inside the header bar under the section name with the chevron
   * staying at its top right.
   */
  legendSlot?: React.ReactNode;
  /** GS11 — content above the rows, inside the open section (the MyFree
   *  lockup and its sign-up button). Absent, the rows start immediately. */
  introSlot?: React.ReactNode;
  /**
   * GS8 — pixels of pinned grid header above this section. An override, not the source, and unset
   * it must write nothing.
   */
  stickyOffset?: number;
  /** Layout only. */
  className?: string;
  /** The section's rows, already built by the caller. */
  children: React.ReactNode;
}

function GridSectionImpl({
  heading,
  headingLevel = "h3",
  defaultOpen = false,
  legendSlot,
  introSlot,
  stickyOffset,
  className,
  children
}: GridSectionProps) {
  const level = safeElement(headingLevel, "h3", TEXT_LEVELS);

  return (
    <div
      className={[styles.section, className].filter(Boolean).join(" ")}
      /* No property at all when the caller sets none — see `stickyOffset`. */
      style={
        stickyOffset === undefined ?
          undefined :
          ({ [GRID_SECTION_OFFSET_PROPERTY]: `${stickyOffset}px` } as React.CSSProperties)
      }
    >
      {/* Zero-size, in normal flow, immediately BEFORE the disclosure — the
          keeper finds the element it watches as its next sibling. */}
      <SectionScrollKeeper className={styles.anchor} offset={stickyOffset} />
      <Disclosure
        className={styles.disclosure}
        defaultOpen={defaultOpen}
        summary={
          <>
            <Text as={level} className={styles.heading} variant="body-2" weight="medium">
              {plainText(heading)}
            </Text>
            {/* Not a bare `&&`: a `ReactNode` slot can be `false`, `""` or `0`, and the wrapper would then paint GS9's legend box over nothing (or the text "0"). Same gate `grid-header.tsx` and `channel-lineup.tsx` apply to their own slots. */}
            {isRenderable(legendSlot) && (
              <span aria-hidden="true" className={styles.legend}>
                {legendSlot}
              </span>
            )}
          </>
        }
        summaryClassName={styles.summary}
      >
        <LazySectionBody className={styles.body} defaultOpen={defaultOpen} introSlot={introSlot}>
          {children}
        </LazySectionBody>
      </Disclosure>
    </div>
  );
}
export const GridSection = withSafeRender(GridSectionImpl, "GridSection");

export default GridSection;
